<template>
  <div class="page-container insurance-plan-page">
    <portal to="page-name">Gerenciamento de plano de saúde</portal>
    <div class="loading loading-lg mt-2" v-if="loading" />
    <div class="card card-page" v-else>
      <div class="card-header">
        <div class="card-title">Gerenciamento de plano de saúde</div>
        <div class="text-dark-gray mt-1">
          Configure período de retorno, regras, impostos, tabelas e outros
        </div>
      </div>
      <div class="card-body">
        <div class="columns">
          <div class="column col-6 col-md-12 col-sm-12 form-group"
               :class="{'has-error': $v.form.name.$error}">
            <label class="form-label">Nome do plano</label>
            <input type="text"
                   class="form-input"
                   v-model="form.name"
                   @blur="$v.form.name.$touch()"
                   placeholder="Nome do plano ou particular"
            >
          </div>
          <div class="column col-6 col-md-12 col-sm-12 form-group"
               :class="{'has-error': $v.form.insurance.id.$error}">
            <label class="form-label">
              Selecione a operadora ou um agrupador
            </label>
            <div class="input-group">
              <select class="form-select"
                      v-model="form.insurance.id"
                      @change="changeInsurance"
                      @blur="$v.form.insurance.id.$touch()"
                      :disabled="!isNew"
              >
                <option value="">Selecione</option>
                <option value="is_new">Cadastrar nova operadora</option>
                <option v-for="(item, i) in insurances"
                        :value="item.id"
                        :key="i"
                >{{ item.name }}
                </option>
              </select>
              <button class="btn btn-gray-outline input-group-btn"
                      :disabled="!isNew"
                      @click="openInsuranceModal">Gerenciar</button>
            </div>
          </div>
          <div class="column col-2 col-md-12 col-sm-12 form-group"
               :class="{'has-error': $v.form.type.$error}">
            <label class="form-label">Tipo</label>
            <select class="form-select"
                    v-model="form.type"
                    @blur="$v.form.type.$touch()"
            >
              <option value="">Selecione</option>
              <option v-for="(text, value) in insuranceTypes"
                      :value="value"
                      :key="value"
              >{{ text }}
              </option>
            </select>
          </div>
          <div class="column col-2 col-md-12 col-sm-12 form-group">
            <label class="form-label">Dias Retorno</label>
            <dx-input-number
              class="form-input text-center"
              v-model="form.returnDays"
              maxlength="2"
              placeholder="00"
            />
          </div>
          <div class="column col-2 col-md-12 col-sm-12 form-group"
               :class="{'has-error': $v.form.record.$error}">
            <label class="form-label">Registro ANS</label>
            <input type="text"
                   class="form-input"
                   v-model="form.record"
                   :disabled="!isInsurance"
                   @blur="$v.form.record.$touch()"
                   v-mask-number
                   placeholder="0000"
            >
          </div>
          <div class="column col-3 col-md-6 col-sm-12 form-group">
            <label class="form-label">
              Faturamento padrão TISS?
            </label>
            <select class="form-select"
                    v-model="form.settings.tiss.enabled"
                    :disabled="!isInsurance">
              <option :value="false">Não, guias genéricas</option>
              <option :value="true">Sim, guias padrão TISS</option>
            </select>
          </div>
          <div class="column col-3 col-md-6 col-sm-12 form-group"
               :class="{'has-error': $v.form.settings.tiss.version.$error}">
            <label class="form-label" for="version">Versão TISS</label>
            <select id="version" class="form-select"
                    v-model="form.settings.tiss.version"
                    :disabled="!isInsurance || !form.settings.tiss.enabled"
                    @blur="$v.form.settings.tiss.version.$touch()">
              <option value="">[Selecione]</option>
              <option v-for="(text, value) in tissVersions"
                      :value="value" :key="value">{{ text }}</option>
            </select>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">Observações</label>
          <textarea class="form-input" rows="2" v-model="form.notes"></textarea>
        </div>

        <st-tabs id="plan-tabs" ref="plan-tabs" class="mt-2">
          <st-tab id="tab-tables" name="Tabelas e Impostos">
            <div class="accordion">
              <input type="checkbox"
                     id="accordion-tables"
                     name="accordion-checkbox" hidden>
              <label class="accordion-header"
                     for="accordion-tables">
                <i class="icon icon-arrow-right mr-1" />
                Tabelas
              </label>
              <div class="accordion-body">
                <div class="accordion-detail">
                  <div class="columns">
                    <div class="column col-6 col-md-12 col-sm-12 form-group">
                      <label class="form-label">Tabela de procedimentos</label>
                      <select class="form-select"
                              v-model="form.settings.general.table.procedureId">
                        <option :value="null">[Selecione]</option>
                        <option v-for="(item, i) in tables"
                                :value="item.id" :key="i">{{ item.name }}</option>
                      </select>
                    </div>
                    <div class="column col-6 col-md-12 col-sm-12 form-group">
                      <label class="form-label">Tabela de exames</label>
                      <select class="form-select"
                              v-model="form.settings.general.table.examId">
                        <option :value="null">[Selecione]</option>
                        <option v-for="(item, i) in tables"
                                :value="item.id" :key="i">{{ item.name }}</option>
                      </select>
                    </div>
                    <div class="column col-6 col-md-12 col-sm-12 form-group">
                      <label class="form-label">Tabela de materiais</label>
                      <select id="product-price" class="form-select"
                              v-model="form.settings.general.table.materialId">
                        <option :value="null">[Selecione]</option>
                        <option v-for="(item, i) in tables"
                                :value="item.id" :key="i">{{ item.name }}</option>
                      </select>
                    </div>
                    <div class="column col-6 col-md-12 col-sm-12 form-group">
                      <label class="form-label">Tabela de medicamentos</label>
                      <select id="product-price" class="form-select"
                              v-model="form.settings.general.table.medicineId">
                        <option :value="null">[Selecione]</option>
                        <option v-for="(item, i) in tables"
                                :value="item.id" :key="i">{{ item.name }}</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="accordion">
              <input type="checkbox"
                     id="accordion-financial"
                     name="accordion-checkbox" hidden>
              <label class="accordion-header"
                     for="accordion-financial">
                <i class="icon icon-arrow-right mr-1" />
                Financeiro
              </label>
              <div class="accordion-body">
                <div class="accordion-detail">
                  <div class="columns">
                    <div class="column col-4 col-md-12 col-sm-12 form-group">
                      <label class="form-label">Forma de pagamento</label>
                      <select class="form-select"
                              v-model="form.settings.general.financial.methodId">
                        <option value="">[Selecione]</option>
                        <option v-for="(item, i) in methods"
                                :value="item.id" :key="i">{{ item.name }}</option>
                      </select>
                    </div>
                    <div class="column col-4 col-md-12 col-sm-12 form-group">
                      <label class="form-label">Categoria</label>
                      <select class="form-select"
                              v-model="form.settings.general.financial.categoryId">
                        <option value="">[Selecione]</option>
                        <option v-for="(item, i) in categories"
                                :value="item.id" :key="i">
                          {{ item.parent.name }} -> {{ item.name }}
                        </option>
                      </select>
                    </div>
                    <div class="column col-4 col-md-12 col-sm-12 form-group">
                      <label class="form-label">
                        Conta bancária / caixa
                      </label>
                      <select class="form-select"
                              v-model="form.settings.general.financial.bankAccountId">
                        <option value="">[Selecione]</option>
                        <option v-for="(item, i) in bankAccounts"
                                :value="item.id" :key="i">{{ item.name }}</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="accordion">
              <input type="checkbox"
                     id="accordion-taxes"
                     name="accordion-checkbox" hidden>
              <label class="accordion-header"
                     for="accordion-taxes">
                <i class="icon icon-arrow-right mr-1" />
                Impostos
              </label>
              <div class="accordion-body">
                <div class="accordion-detail">
                  <div class="columns">
                    <div class="column col-md-4 col-sm-6 form-group">
                      <label class="form-label">ISS</label>
                      <dx-input-number class="form-input"
                                       v-model="form.settings.general.taxes.iss"
                                       :precision="2"
                      />
                    </div>
                    <div class="column col-md-4 col-sm-6 form-group">
                      <label class="form-label">IRPJ</label>
                      <dx-input-number class="form-input"
                                       v-model="form.settings.general.taxes.irpj"
                                       :precision="2"
                      />
                    </div>
                    <div class="column col-md-4 col-sm-6 form-group">
                      <label class="form-label">PIS</label>
                      <dx-input-number class="form-input"
                                       v-model="form.settings.general.taxes.pis"
                                       :precision="2"
                      />
                    </div>
                    <div class="column col-md-4 col-sm-6 form-group">
                      <label class="form-label">CSLL</label>
                      <dx-input-number class="form-input"
                                       v-model="form.settings.general.taxes.csll"
                                       :precision="2"
                      />
                    </div>
                    <div class="column col-md-4 col-sm-6 form-group">
                      <label class="form-label">COFINS</label>
                      <dx-input-number class="form-input"
                                       v-model="form.settings.general.taxes.cofins"
                                       :precision="2"
                      />
                    </div>
                    <div class="column col-md-4 col-sm-6 form-group">
                      <label class="form-label">ME/EPP</label>
                      <dx-input-number class="form-input"
                                       v-model="form.settings.general.taxes.epp"
                                       :precision="2"
                      />
                    </div>
                    <div class="column col-md-4 col-sm-6 form-group">
                      <label class="form-label">Admin</label>
                      <dx-input-number class="form-input"
                                       v-model="form.settings.general.taxes.admin"
                                       :precision="2"
                      />
                    </div>
                    <div class="column col-md-4 col-sm-6 form-group">
                      <label class="form-label">Outros</label>
                      <dx-input-number class="form-input"
                                       v-model="form.settings.general.taxes.others"
                                       :precision="2"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </st-tab>
          <st-tab id="tab-params" name="Parâmetros">
            <div class="accordion">
              <div class="accordion-detail">
                <input type="checkbox"
                       id="accordion-params-appointments"
                       name="accordion-checkbox" hidden>
                <label class="accordion-header"
                       for="accordion-params-appointments">
                  <i class="icon icon-arrow-right mr-1" />
                  Agenda
                </label>
                <div class="accordion-body">
                  <div class="accordion-detail">
                    <div class="stenci-title">Carteirinha</div>
                    <div class="divider" />
                    <label class="form-switch d-inline-block">
                      <input type="checkbox"
                             v-model="form.settings.general.record.required">
                      Exigir preenchimento da carteirinha
                      <i class="form-icon"></i>
                    </label><br>
                    <label class="form-switch d-inline-block">
                      <input type="checkbox"
                             v-model="form.settings.general.record.requiredValidity">
                      Exigir preenchimento da validade
                      <i class="form-icon"></i>
                    </label><br>
                    <div class="form-group d-flex">
                      <label class="form-switch">
                        <input type="checkbox"
                               v-model="form.settings.general.record.digits.enabled">
                        Número de dígitos da carteirinha:
                        <i class="form-icon"></i>
                      </label>
                      <div class="mr-2" style="max-width: 50px;"
                           :class="{'has-error': $v.form.settings.general
                           .record.digits.min.$error}">
                        <dx-input-number id="total"
                                         class="form-input input-sm text-center"
                                         maxlength="3"
                                         placeholder="mín"
                                         :disabled="!recordDigitsActive"
                                         @blur="$v.form.settings.general
                                         .record.digits.min.$touch()"
                                         v-model="form.settings.general.record.digits.min"
                        />
                      </div>
                      <div style="max-width: 50px;"
                           :class="{'has-error': $v.form.settings.general
                           .record.digits.max.$error}">
                        <dx-input-number id="total"
                                         class="form-input input-sm text-center"
                                         :class="{'has-error': $v.form.settings.general
                                     .record.digits.max.$error}"
                                         maxlength="3"
                                         style="max-width: 50px;"
                                         placeholder="máx"
                                         :disabled="!recordDigitsActive"
                                         @blur="$v.form.settings.general
                                         .record.digits.max.$touch()"
                                         v-model="form.settings.general.record.digits.max"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="accordion" v-if="form.settings.tiss.enabled">
              <div class="accordion-detail">
                <input type="checkbox"
                       id="accordion-billing"
                       name="accordion-checkbox" hidden>
                <label class="accordion-header"
                       for="accordion-billing">
                  <i class="icon icon-arrow-right mr-1" />
                  Faturamento XML
                </label>
                <div class="accordion-body">
                  <div class="accordion-detail">
                    <div class="stenci-title">Faturamento</div>
                    <div class="divider" />
                    <label class="form-switch d-inline-block">
                      <input type="checkbox"
                             v-model="form.settings.general.record.fillValidityWithServiceDate">
                      Preencher a data de validade da carteirinha com a data do atendimento
                      <i class="form-icon"></i>
                    </label><br>
                    <label class="form-switch d-inline-block">
                      <input type="checkbox"
                             v-model="form.settings.general.billing.blockValues">
                      Bloquear edição dos campos valores
                      <i class="form-icon"></i>
                    </label><br>
                    <label class="form-switch d-inline-block">
                      <input type="checkbox"
                             v-model="form.settings.general.billing.hideInsuranceValue">
                      Ocultar valores dos convênios
                      <i class="form-icon"></i>
                    </label><br>
                    <label class="form-switch d-inline-block">
                      <input type="checkbox"
                             v-model="form.settings.general.billing.allowDifferentReferralTypes">
                      Permitir faturar diferentes guias de atendimento na mesma fatura
                      <i class="form-icon"></i>
                    </label><br>
                    <label class="form-switch d-inline-block">
                      <input type="checkbox"
                             v-model="form.settings.general.billing.billingForIndividualSessions">
                      Faturamento por sessões individualizadas
                      <i class="form-icon"></i>
                    </label><br>
                    <label class="form-switch d-inline-block">
                      <input type="checkbox"
                             v-model="form.settings.general.billing.return.enabled">
                      Solicitar senha de liberação para retorno acima do prazo máximo
                      <i class="form-icon"></i>
                    </label>
                    <div class="d-flex">
                      <label class="form-switch d-inline-block">
                        <input type="checkbox"
                               v-model="form.settings.general.billing.accounts.enabled">
                        Quantidade de guias por fatura
                        <i class="form-icon"></i>
                      </label>
                      <div style="max-width: 50px;"
                           :class="{'has-error': $v.form.settings.general
                       .billing.accounts.quantity.$error}">
                        <dx-input-number id="total"
                                         class="form-input input-sm text-center"
                                         maxlength="3"
                                         :disabled="!billingAccountsActive"
                                         @blur="$v.form.settings.general
                                     .billing.accounts.quantity.$touch()"
                                         v-model="form.settings.general.billing.accounts.quantity"
                        />
                      </div>
                    </div>
                    <div class="text-bold mt-2">Guia Prestador</div>
                    <label class="form-radio">
                      <input name="radio-provider-guide"
                             type="radio"
                             v-model="form.settings.general.providerReferral.mode"
                             value="manual">
                      <i class="form-icon"></i>Informar o número da guia prestador manualmente
                    </label>
                    <div class="d-flex">
                      <label class="form-radio">
                        <input name="radio-provider-guide"
                               type="radio"
                               v-model="form.settings.general.providerReferral.mode"
                               value="automatic">
                        <i class="form-icon"></i>
                        Gerar o número da guia prestador automaticamente. Guia atual:
                      </label>
                      <div class="mr-2" style="max-width: 200px;"
                           :class="{'has-error': $v.form.settings.general
                       .providerReferral.lastValue.$error}">
                        <input type="text"
                               class="form-input input-sm text-center"
                               :disabled="!providerReferralActive"
                               @blur="$v.form.settings.general.providerReferral.lastValue.$touch()"
                               v-model="form.settings.general.providerReferral.lastValue"
                               v-mask="'000000000000000'"
                        />
                      </div>
                    </div>
                    <label class="form-radio">
                      <input name="radio-provider-guide"
                             type="radio"
                             v-model="form.settings.general.providerReferral.mode"
                             value="password">
                      <i class="form-icon"></i>Preencher a guia prestador com a senha
                    </label>
                    <label class="form-radio">
                      <input name="radio-provider-guide"
                             type="radio"
                             v-model="form.settings.general.providerReferral.mode"
                             value="insurance_referral">
                      <i class="form-icon"></i>Preencher a guia prestador com a guia operadora
                    </label>

                    <div class="text-bold mt-2">Tipo de contrato</div>
                    <div class="divider" />
                    <div class="columns">
                      <div class="column col-1 col-md-2 col-sm-2 form-group">
                        <label class="form-label" for="type">Tipo</label>
                        <select id="type" class="form-select"
                                v-model="form.settings.tiss.contract.type">
                          <option value="person">PF</option>
                          <option value="company">PJ</option>
                        </select>
                      </div>
                      <div class="column col-md-5 col-sm-12 form-group"
                           :class="{'has-error': $v.form.settings.tiss.contract.code.$error}">
                        <label class="form-label" for="operator">Código na operadora</label>
                        <input type="text" id="operator" class="form-input"
                               v-model="form.settings.tiss.contract.code"
                               :disabled="!isCompany"
                               @blur="$v.form.settings.tiss.contract.code.$touch()" />
                      </div>
                      <div class="column col-md-5 col-sm-12 form-group"
                           :class="{'has-error': $v.form.settings.tiss.contract.identity.$error}">
                        <label class="form-label" for="identity">CNPJ</label>
                        <input type="text" id="identity" class="form-input"
                               v-model="form.settings.tiss.contract.identity"
                               v-mask="'00.000.000/0000-00'"
                               :disabled="!isCompany"
                               @blur="$v.form.settings.tiss.contract.identity.$touch()" />
                      </div>
                      <div class="column col-md-7 col-sm-12 form-group"
                           :class="{'has-error': $v.form.settings.tiss.contract.name.$error}">
                        <label class="form-label" for="name">Nome</label>
                        <input type="text" id="name" class="form-input"
                               v-model="form.settings.tiss.contract.name"
                               :disabled="!isCompany"
                               @blur="$v.form.settings.tiss.contract.name.$touch()" />
                      </div>
                      <div class="column col-2 col-md-3 col-sm-12 form-group"
                           :class="{'has-error': $v.form.settings.tiss.contract.cnes.$error}">
                        <label class="form-label" for="cnes">CNES</label>
                        <input type="text" id="cnes" class="form-input"
                               v-model="form.settings.tiss.contract.cnes"
                               :disabled="!isCompany"
                               @blur="$v.form.settings.tiss.contract.cnes.$touch()" />
                      </div>
                      <div class="column col-auto mb-2"
                           style="align-self: flex-end">
                        <button class="btn btn-gray btn-icon btn-action tooltip tooltip-left"
                                data-tooltip="Preencher os campos com os dados da empresa"
                                :disabled="!isCompany || loadingBranch"
                                @click="getCompanyData">
                          <fa-icon :icon="['fal', 'sync']" />
                        </button>
                      </div>
                    </div>

                    <div class="text-bold mt-2">Preenchimento da guia</div>
                    <div class="divider" />
                    <div class="columns">
                      <div class="column col-3 col-md-6 col-sm-12 form-group">
                        <label class="form-label" for="origin">Origem</label>
                        <select id="origin" class="form-select"
                                v-model="referralOrigin">
                          <option v-for="(text) in tissTags"
                                  :value="text.key" :key="text.key">{{ text.name }}</option>
                        </select>
                      </div>
                      <div class="column col-3 col-md-6 col-sm-12 form-group">
                        <label class="form-label" for="requester">Dados do solicitante</label>
                        <select id="requester" class="form-select"
                                v-model="referralRequester">
                          <option v-for="(text) in tissTags"
                                  :value="text.key" :key="text.key">{{ text.name }}</option>
                        </select>
                      </div>
                      <div class="column col-3 col-md-6 col-sm-12 form-group">
                        <label class="form-label" for="executant">Dados do executante</label>
                        <select id="executant" class="form-select"
                                v-model="referralExecutant">
                          <option v-for="(text) in tissTags"
                                  :value="text.key" :key="text.key">{{ text.name }}</option>
                        </select>
                      </div>
                      <div class="column col-3 col-md-6 col-sm-12 form-group">
                        <label class="form-label" for="professional-executant">
                          Profissional executante
                        </label>
                        <select id="professional-executant" class="form-select"
                                v-model="referralProfessional">
                          <option v-for="(text) in tissTags"
                                  :value="text.key" :key="text.key">{{ text.name }}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="accordion">
              <div class="accordion-detail">
                <input type="checkbox"
                       id="accordion-others"
                       name="accordion-checkbox" hidden>
                <label class="accordion-header"
                       for="accordion-others">
                  <i class="icon icon-arrow-right mr-1" />
                  Outros
                </label>
                <div class="accordion-body">
                  <div class="accordion-detail">
                    <div class="stenci-title">ASO</div>
                    <div class="divider" />
                    <label class="form-switch d-inline-block">
                      <input type="checkbox"
                             v-model="form.settings.general.aso.requiredClient">
                      Exigir preenchimento da empresa na agenda
                      <i class="form-icon"></i>
                    </label>
                  </div>
                </div>
              </div>
            </div>

<!--              <div class="column col-4 col-md-12 col-sm-12 form-group">-->
<!--                <div class="text-info text-bold">Redutores</div>-->
<!--                <table class="table">-->
<!--                  <thead>-->
<!--                  <tr>-->
<!--                    <th>Descrição</th>-->
<!--                    <th class="text-right">Percentual</th>-->
<!--                  </tr>-->
<!--                  </thead>-->
<!--                  <tbody>-->
<!--                  <tr v-for="(item, i) in reducers" :key="i">-->
<!--                    <td>{{ item.name }}</td>-->
<!--                    <td class="text-right" style="width: 130px">-->
<!--                      <div class="input-group">-->
<!--                        <input-->
<!--                          type="text"-->
<!--                          class="form-input text-right"-->
<!--                          v-model="item.value"-->
<!--                          :readonly="!item.enabled"-->
<!--                          @blur="updateReducer(item.value, i)"-->
<!--                          v-mask="'000'"-->
<!--                        >-->
<!--                        <span class="input-group-addon text-gray">%</span>-->
<!--                      </div>-->
<!--                    </td>-->
<!--                  </tr>-->
<!--                  </tbody>-->
<!--                </table>-->
<!--              </div>-->
          </st-tab>
          <st-tab id="tab-expenses" name="Serviços, taxas e produtos">
            <div class="empty mt-2" v-if="expenses.length === 0">
              <div class="empty-icon">
                <fa-icon :icon="['fal', 'info-circle']" size="3x"/>
              </div>
              <p class="empty-title h5">Despesas</p>
              <p class="empty-subtitle">
                Nenhuma despesa cadastrada ou nenhuma tabela vinculada ao plano.
              </p>
            </div>
            <template v-else>
              <div class="stenci-group">
                <div class="columns">
                  <div class="column col-4 col-md-6 col-md-12 form-group">
                    <label class="form-label">Filtrar tipo</label>
                    <select class="form-select" v-model="filter.type">
                      <option value="">Todos</option>
                      <option v-for="(text, value) in expenseTypes"
                              :value="value" :key="value">{{ text }}</option>
                    </select>
                  </div>
                  <div class="column form-group">
                    <label class="form-label">Pesquisar</label>
                    <div class="input-group">
                      <input
                        type="text"
                        class="form-input"
                        v-model="filter.search"
                        placeholder="Pesquisar por nome da despesa">
                      <button class="btn btn-neutral btn-action input-group-btn btn-icon"
                              tabindex="-1">
                        <fa-icon :icon="['fal', 'search']" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div class="stenci-title">Lista de serviços, taxas e produtos</div>
              <div class="divider" />
              <small>
                Para alterar as regras e valores das despesas, você precisa editar a
                tabela vinculada ao plano de saúde.
              </small>
              <div class="empty mt-2" v-if="filteredExpenses.length === 0">
                <div class="empty-icon">
                  <fa-icon :icon="['fal', 'info-circle']" size="3x"/>
                </div>
                <p class="empty-title h5">Despesas</p>
                <p class="empty-subtitle">
                  Nenhuma despesa encontrada com este filtro.
                </p>
              </div>
              <table class="table table-hover" v-else>
                <thead>
                <tr>
                  <th class="hide-md hide-sm" style="width: 40px">#</th>
                  <th class="hide-md hide-sm" style="width: 90px">Código</th>
                  <th>Descrição</th>
                  <th class="text-center hide-md hide-sm" style="width: 60px">Tabela</th>
                  <th class="text-right" style="width: 120px">Valor plano</th>
                  <th class="text-right" style="width: 120px">Valor paciente</th>
                  <th class="text-right" style="width: 80px">Repasse</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(item, i) in filteredExpenses" :key="i">
                  <td class="text-center hide-md hide-sm">{{ i + 1 }}</td>
                  <td class="text-center hide-md hide-sm">{{ item.code | tuss }}</td>
                  <td>{{ item.expense.name }}</td>
                  <td class="text-center hide-md hide-sm">{{ item.tableCode }}</td>
                  <td class="text-right">
                    {{ item.definition.insuranceValue | currency }}
                  </td>
                  <td class="text-right">
                    {{ item.definition.particularValue | currency }}
                  </td>
                  <td class="text-right">
                    {{ item.definition.transferValue | number }}
                  </td>
                </tr>
                </tbody>
              </table>
            </template>
          </st-tab>
        </st-tabs>
      </div>
      <div class="card-footer">
        <button class="btn btn-primary btn-icon btn-icon-left mr-1"
                @click="save" :disabled="saving"
                :class="{loading: saving}">
          <fa-icon :icon="['fal', 'save']" />Salvar
        </button>
        <button class="btn" @click="$router.push('/clinical/insurance-plans')">
          Voltar
        </button>
      </div>
    </div>

    <insurance-modal
      v-if="insuranceModal.show"
      :is-new="insuranceModal.isNew"
      @save="saveInsurance"
      @remove="removeInsurance"
      @close="closeInsuranceModal"
    />

  </div>
</template>

<script>
import { tissTags } from '@/data/tiss-tags';
import { types as expenseTypes } from 'src/data/expense-types';
import { types as insuranceTypes } from 'src/data/insurance-types';
import { tissVersions } from '@/data/tiss-versions';
import { minValue, required } from 'vuelidate/lib/validators';
import { cnpj } from 'src/data/validators';
import { mergeFrom } from '@/helpers/object';
import fuzzy from 'fuzzy';
import { mapState } from 'vuex';
import formMixin from '../../../mixins/form';
import InsuranceModal from './modals/InsuranceModal.vue';

export default {
  mixins: [formMixin],
  components: { InsuranceModal },
  data() {
    return {
      loading: false,
      loadingBranch: false,
      saving: false,
      isNew: false,
      expenseTypes,
      insuranceTypes,
      tissVersions,
      tissTags,
      bankAccounts: [],
      categories: [],
      expenses: [],
      tables: [],
      methods: [],
      insurances: [],
      filter: {
        type: '',
        search: '',
      },
      insuranceModal: {
        show: false,
        isNew: false,
      },
      reducers: [
        { name: 'Primeira despesa', value: 100, enabled: false },
        { name: 'Segunda despesa', value: 70, enabled: true },
        { name: 'Terceira despesa', value: 50, enabled: true },
        { name: 'Demais despesas', value: 50, enabled: true },
      ],
      form: this.blankForm(),
    };
  },
  async mounted() {
    await this.init();
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    isInsurance() {
      return this.form.type === 'insurance';
    },
    isCompany() {
      return this.form.settings.tiss.contract.type === 'company';
    },
    recordDigitsActive() {
      return this.form.settings.general.record.digits.enabled;
    },
    billingAccountsActive() {
      return this.form.settings.general.billing.accounts.enabled;
    },
    filteredExpenses() {
      let { expenses } = this;

      if (this.filter.type) {
        expenses = expenses.filter(item => item.expense.type === this.filter.type);
      }

      const search = this.filter.search.trim();

      if (search) {
        const results = fuzzy.filter(search, expenses, {
          extract: (el) => {
            if (el.expense.name) {
              return this.removeAccentuation(el.expense.name);
            }
            return '';
          },
        });
        expenses = results.map(({ original }) => original);
      }

      return expenses;
    },
    providerReferralActive() {
      return this.form.settings.general.providerReferral.mode === 'automatic';
    },
    referralOrigin: {
      get() {
        return this.encodeTypeValue(this.form.settings.tiss.referral.origin);
      },
      set(value) {
        this.form.settings.tiss.referral.origin = this.decodeTypeValue(value);
      },
    },
    referralRequester: {
      get() {
        return this.encodeTypeValue(this.form.settings.tiss.referral.requester);
      },
      set(value) {
        this.form.settings.tiss.referral.requester = this.decodeTypeValue(value);
      },
    },
    referralExecutant: {
      get() {
        return this.encodeTypeValue(this.form.settings.tiss.referral.executant);
      },
      set(value) {
        this.form.settings.tiss.referral.executant = this.decodeTypeValue(value);
      },
    },
    referralProfessional: {
      get() {
        return this.encodeTypeValue(this.form.settings.tiss.referral.professional);
      },
      set(value) {
        this.form.settings.tiss.referral.professional = this.decodeTypeValue(value);
      },
    },
  },
  validations() {
    const rules = {
      form: {
        type: { required },
        name: { required },
        record: {},
        insurance: {
          id: { required },
        },
        settings: {
          general: {
            record: {
              digits: {
                min: {},
                max: {},
              },
            },
            billing: {
              accounts: {
                quantity: {},
              },
            },
            providerReferral: {
              lastValue: {},
            },
          },
          tiss: {
            version: {},
            xml: {
              batch: {},
            },
            contract: {
              code: {},
              identity: { cnpj },
              name: {},
              cnes: {},
            },
            sadt: {
              authorization: {
                fillValidityDate: {
                  days: {},
                },
              },
            },
          },
        },
      },
    };

    if (this.isInsurance) {
      rules.form.record = { required };

      if (this.form.settings.tiss.enabled) {
        rules.form.settings.tiss.version = { required };
      }
    }

    const useTiss = !!this.form.settings.tiss.enabled;

    if (useTiss && this.form.settings.general.record.digits.enabled) {
      rules.form.settings.general.record.digits.min = {
        required,
        minValue: minValue(1),
      };
      rules.form.settings.general.record.digits.max = {
        required,
        minValue: minValue(1),
      };
      if (this.form.settings.general.record.digits.min > 0) {
        rules.form.settings.general.record.digits.max = {
          required,
          minValue: minValue(this.form.settings.general.record.digits.min),
        };
      }
    }

    if (useTiss && this.form.settings.general.billing.accounts.enabled) {
      rules.form.settings.general.billing.accounts.quantity = {
        required,
        minValue: minValue(1),
      };
    }

    if (useTiss && this.form.settings.general.providerReferral.mode === 'automatic') {
      rules.form.settings.general.providerReferral.lastValue = {
        required,
        minValue: minValue(1),
      };
    }

    if (useTiss && !this.form.settings.tiss.xml.batch) {
      rules.form.settings.tiss.xml.batch = {
        required,
        minValue: minValue(1),
      };
    }

    if (useTiss && !this.form.settings.tiss.version) {
      rules.form.settings.tiss.version = { required };
    }

    if (useTiss && this.form.settings.tiss.contract.type === 'company') {
      if (!this.form.settings.tiss.contract.code) {
        rules.form.settings.tiss.contract.code = { required };
      }
      if (!this.form.settings.tiss.contract.identity) {
        rules.form.settings.tiss.contract.identity = { required };
      }
      if (!this.form.settings.tiss.contract.name) {
        rules.form.settings.tiss.contract.name = { required };
      }
      if (!this.form.settings.tiss.contract.cnes) {
        rules.form.settings.tiss.contract.cnes = { required };
      }
    }

    if (useTiss
      && this.form.settings.tiss.sadt.authorization.fillValidityDate.enabled) {
      rules.form.settings.tiss.sadt.authorization.fillValidityDate.days = {
        required,
        minValue: minValue(1),
      };
    }

    return rules;
  },
  methods: {
    async init() {
      this.isNew = /create$/.test(this.$route.path);
      this.loadBankAccounts();
      this.loadCategories();
      this.loadMethods();
      this.loadTables();
      this.loadInsurances();

      if (!this.isNew) {
        await this.load();
        this.loadExpenses();
      }

      this.$v.form.$touch();
    },
    load() {
      this.loading = true;
      const { insuranceId } = this.$route.params;
      const planId = this.$route.params.id;

      return this.$http
        .get(`/insurances/${insuranceId}/plans/${planId}`)
        .then(({ data }) => {
          this.form = mergeFrom(this.blankForm(), data);
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro ao carregar o plano. Tente novamente!');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadBankAccounts() {
      const params = {
        active: true,
        limit: 0,
      };

      return this.$http
        .get('/bank-accounts', { params })
        .then(({ data }) => {
          this.bankAccounts = data?.items || [];
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro. Tente novamente!');
        })
        .finally({});
    },
    loadCategories() {
      const params = {
        active: true,
        allowsValue: true,
        limit: 0,
      };

      return this.$http
        .get('/categories', { params })
        .then(({ data }) => {
          this.categories = data?.items || [];
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro. Tente novamente!');
        })
        .finally({});
    },
    loadMethods() {
      const params = {
        active: true,
        limit: 0,
      };

      return this.$http
        .get('/payment-methods', { params })
        .then(({ data }) => {
          this.methods = data?.items || [];
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro. Tente novamente!');
        })
        .finally({});
    },
    loadTables() {
      const params = {
        active: true,
      };

      return this.$http
        .get('/expense-tables', { params })
        .then(({ data }) => {
          this.tables = data?.items || [];
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro. Tente novamente!');
        })
        .finally({});
    },
    loadExpenses() {
      const params = {
        insuranceId: this.form.insurance.id,
        planId: this.form.id,
        origin: 'plan',
        limit: 1000,
      };

      return this.$http
        .get('/expense-insurances', { params })
        .then(({ data }) => {
          this.expenses = data?.items || [];
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro. Tente novamente!');
        })
        .finally({});
    },
    loadInsurances() {
      const params = {
        active: true,
        limit: 0,
      };

      return this.$http.get('/insurances', { params })
        .then(({ data }) => {
          this.insurances = data?.items || [];
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro. Tente novamente!');
        })
        .finally({});
    },
    async save() {
      this.$v.form.$touch();
      if (this.$v.form.$error) {
        return;
      }

      this.saving = true;

      const data = this.clone(this.form);
      const insuranceId = data.insurance.id;
      const planId = data.id;

      if (!data.companyName) {
        delete data.companyName;
      }

      if (!data?.identity?.value) {
        delete data.identity;
      }

      if (!data.record) {
        delete data.record;
      }

      const request = this.isNew
        ? this.$http.post(`/insurances/${insuranceId}/plans`, data)
        : this.$http.put(`/insurances/${insuranceId}/plans/${planId}`, data);

      // eslint-disable-next-line consistent-return
      return request
        .then(({ data: result }) => {
          if (this.isNew) {
            this.$router.replace(`/clinical/insurances/${insuranceId}/plans/${planId}/edit`);
            this.isNew = false;
            this.form.id = result.id;
          }
          this.$toast.show('Registro salvo!');
        })
        .catch(() => {})
        .finally(() => {
          this.saving = false;
        });
    },
    openInsuranceModal() {
      this.insuranceModal.isNew = false;
      this.insuranceModal.show = true;
    },
    changeInsurance() {
      if (this.form.insurance.id === 'is_new') {
        this.insuranceModal.isNew = true;
        this.insuranceModal.show = true;
      }
    },
    async saveInsurance(insurance) {
      await this.loadInsurances();
      const found = this.insurances.find(item => item.id === insurance.id);

      if (found) {
        this.form.insurance = found;
      }
      this.insuranceModal.show = false;
    },
    closeInsuranceModal() {
      this.form.insurance = { id: '', name: '' };
      this.insuranceModal.show = false;
    },
    removeInsurance(id) {
      this.form.insurance = { id: '', name: '' };
      const found = this.insurances.find(item => item.id === id);
      this.insurances.splice(this.insurances.indexOf(found), 1);
    },
    getCompanyData() {
      this.loadingBranch = true;

      return this.$http.get(`/branches/${this.user.branch.id}`)
        .then(({ data }) => {
          if (data.identity) {
            if (data.identity.type === 'cnpj') {
              this.form.settings.tiss.contract.code = data.identity.value;
              this.form.settings.tiss.contract.identity = data.identity.value;
            }
          }
          if (data.companyName) {
            this.form.settings.tiss.contract.name = data.companyName;
          }
          if (data.branch) {
            this.form.settings.tiss.contract.cnes = data.branch.cnes;
          }
        })
        .catch(() => {})
        .finally(() => {
          this.loadingBranch = false;
        });
    },
    decodeTypeValue(str) {
      const [type, value] = str.split('_');
      return { type, value };
    },
    encodeTypeValue({ type, value }) {
      return [type, value].join('_');
    },
    blankForm() {
      return {
        id: null,
        name: '',
        type: '',
        record: '',
        active: true,
        returnDays: '',
        dailyLimit: '',
        notes: '',
        insurance: {
          id: '',
          name: '',
        },
        settings: {
          general: this.blankGeneralForm(),
          tiss: this.blankTissForm(),
        },
      };
    },
    blankGeneralForm() {
      return {
        aso: {
          requiredClient: false,
        },
        record: {
          required: false,
          requiredValidity: false,
          fillValidityWithServiceDate: false,
          digits: {
            enabled: false,
            min: '',
            max: '',
          },
        },
        billing: {
          blockValues: false,
          hideInsuranceValue: false,
          allowDifferentReferralTypes: false,
          billingForIndividualSessions: false,
          accounts: {
            enabled: false,
            quantity: '',
          },
          return: {
            enabled: false,
            days: '',
          },
          reducers: [],
        },
        financial: {
          methodId: '',
          categoryId: '',
          bankAccountId: '',
        },
        taxes: {
          iss: '',
          irpj: '',
          pis: '',
          csll: '',
          cofins: '',
          epp: '',
          admin: '',
          others: '',
        },
        providerReferral: {
          lastValue: '',
          mode: 'automatic',
        },
        table: {
          procedureId: null,
          examId: null,
          materialId: null,
          medicineId: null,
        },
      };
    },
    blankTissForm() {
      return {
        enabled: false,
        version: '',
        xml: {
          batch: '',
          fields: {
            insuranceReferral: false,
          },
        },
        contract: {
          type: 'company',
          code: '',
          identity: '',
          name: '',
          cnes: '',
        },
        referral: {
          origin: {
            type: 'company',
            value: 'identity',
          },
          requester: {
            type: 'company',
            value: 'identity',
          },
          executant: {
            type: 'company',
            value: 'identity',
          },
          professional: {
            type: 'company',
            value: 'identity',
          },
        },
        consultation: {
          sadt: false,
          requiredInsuranceReferral: false,
        },
        sadt: {
          fillRequestDataWithServiceDate: false,
          fillAuthorizationDateWithServiceDate: false,
          requiredRequestDate: false,
          authorization: {
            fillValidityDate: {
              enabled: false,
              days: '',
            },
            fillInsuranceReferralWithPassword: false,
            requiredMainReferral: false,
            requiredInsuranceReferral: false,
            requiredDate: false,
            requiredPassword: false,
            requiredValidity: false,
          },
        },
      };
    },
  },
};
</script>

<style lang="scss">
  @import '@/assets/scss/_variables.scss';
  .insurance-plan-page {
    .accordion {
      background-color: $gray-color-ultra-light;
      margin-top: $layout-spacing-lg;
      padding: $layout-spacing-sm;
      .accordion-header {
        cursor: pointer;
        font-weight: 500;
      }
      .accordion-body {
        margin: $layout-spacing;
        .accordion-detail {
          border-top: $unit-h solid $border-color;
          padding-top: $layout-spacing;
        }
      }
    }
    .table {
      margin-top: $layout-spacing;
    }
    .table td {
      font-size: .55rem;
    }
    .table th {
      background-color: $gray-color-ultra-light;
    }
  }
</style>
